import * as React from "react";
import { useFormContext } from "react-hook-form";
import { NGLabelText } from "../../../newcomponents/text/labelText";
import { linksOf } from "../../../services/cpln";
import { Workload } from "../../../schema/types/workload/workload";
import { ngParseLink } from "../../../utils/linkParser/linkParser";
import clsx from "clsx";
import { NGButton } from "../../../newcomponents/button/Button";
import { useDebounce } from "../../../components/table/useDebounce";
import { DomainDryRun } from "./domainDryRun";
import { FormDryRunAlert } from "./formDryRunAlert";
import { domainBasicToAdvanced } from "./utils/utils";
import { v4 as uuidv4 } from "uuid";
import { CreateNGDryRunChecker } from "./create_ng_dryRunChecker";
import { CreateNGSaveCreate } from "./create_ng_saveCreate";
import { CreateNGCancelReset } from "./craete_ng_cancelReset";
import { Domain } from "../../../schema/types/domain/entity";
import { FormNGInput } from "./components/FormNGInput";
import { FormNGKindSelect } from "./components/FormNGKindSelect";
import { DomainInput } from "./types/input";

interface Props {
  domain?: Domain;
}
export const CreateNGBasic: React.FC<Props> = ({ domain }) => {
  const { control, watch, reset, setValue, getValues } = useFormContext<DomainInput>();

  const isEdit = watch("isEdit") || false;
  const name = watch("basic.name") || "";
  const workloadLink = watch("basic.workload.link") || "";

  const [valuesDebounced, setValuesDebounced] = useDebounce<number>(0, 300);
  function triggerDryRunCheck() {
    setValuesDebounced((x) => x + 1);
  }
  React.useEffect(() => {
    triggerDryRunCheck();
  }, [name, workloadLink]);
  React.useEffect(() => {
    setValue("dryRun.triggerer", uuidv4());
  }, [valuesDebounced, setValue]);

  return (
    <>
      {isEdit ? (
        <div className="mb-8">
          <NGLabelText>Domain</NGLabelText>
          <div>{name}</div>
        </div>
      ) : (
        <div className="mb-8">
          <div className="flex items-center">
            <NGLabelText>Domain</NGLabelText>
            <span className="color-danger">*</span>
          </div>
          <FormNGInput
            name="basic.name"
            control={control}
            required
            data-testid={`basic-domain`}
            style={{ width: 600 }}
          />
          <FormDryRunAlert path={"name"} />
        </div>
      )}
      <div className="mb-8">
        <NGLabelText>Description</NGLabelText>
        <FormNGInput name="basic.description" control={control} style={{ width: 600 }} />
        <FormDryRunAlert path={"description"} />
      </div>
      <div className="mb-8">
        <div className="flex items-center">
          <NGLabelText>Workload</NGLabelText>
          <span className="color-danger">*</span>
        </div>
        <FormNGKindSelect<Workload>
          name="basic.workload.link"
          control={control}
          rules={{ required: "Workload is required" }}
          kind="workload"
          optionValueAs="absolute"
          onChangeExtra={(link, item) => {
            const { name, gvc, org } = ngParseLink(link, { useInputCtx: true });
            setValue("basic.workload.name", name);
            setValue("basic.workload.item", item);
            setValue("gvc.name", gvc);
            setValue("gvc.link", `/org/${org}/gvc/${gvc}`);
          }}
          style={{ width: 600 }}
          renderOption={({
            option,
            getOptionLabel,
            getOptionValue,
            isHovered,
            isSelected,
            isDisabled,
            isLabel,
            props,
          }) => {
            const gvcLink = linksOf(option).gvc!;
            const { name: gvcName } = ngParseLink(gvcLink, { useInputCtx: true });
            return (
              <li
                key={getOptionValue(option)}
                {...props}
                className={clsx(`flex items-center color-default justify-between`, {
                  "option-hover": isHovered,
                  "option-selected": isSelected,
                  "option-label": isLabel,
                  "option-disabled": isDisabled,
                  "cursor-not-allowed": isLabel || isDisabled,
                })}
              >
                <span className="truncate">{getOptionLabel(option)}</span>
                <div className="flex items-center gap-1 text-sm">
                  <NGLabelText className="text-sm">GVC</NGLabelText>
                  <span>{gvcName}</span>
                </div>
              </li>
            );
          }}
        />
        <FormDryRunAlert path={"spec.ports[0].routes[0].workloadLink"} />
      </div>
      {workloadLink ? (
        <DomainDryRun key={"dryRun"} dryRunPath="raw" showValid={true} />
      ) : isEdit ? null : (
        <DomainDryRun key={"dryRunNameOnly"} dryRunPath="nameOnly" showValid={false} />
      )}
      <CreateNGDryRunChecker />
      <div className="grid grid-cols-3 gap-4 mt-6" style={{ width: 600 }}>
        <CreateNGCancelReset domain={domain} />
        <NGButton
          variant="secondary"
          className="w-full"
          type={`button`}
          onClick={() => reset(domainBasicToAdvanced(getValues()))}
        >
          Advanced Settings
        </NGButton>
        <CreateNGSaveCreate domain={domain} />
      </div>
    </>
  );
};
